"use client";

import { Button, ButtonProps } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/clientUtils";
import { useFormStatus } from "react-dom";
export function StandardTitle({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <h1 className={cn("text-2xl leading-tight", className)} data-sentry-component="StandardTitle" data-sentry-source-file="standards.tsx">{children}</h1>;
}
export function StandardSubtitle({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <p className={cn(" text-gray-500", className)} data-sentry-component="StandardSubtitle" data-sentry-source-file="standards.tsx">{children}</p>;
}
export function StandardBody({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <p className={cn("text-[#152B6F] leading-[1.5]", className)} data-sentry-component="StandardBody" data-sentry-source-file="standards.tsx">{children}</p>;
}
export function StandardLabel({
  label,
  children,
  className,
  error
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
  error?: string;
}) {
  return <label className={cn("block", className)} data-sentry-component="StandardLabel" data-sentry-source-file="standards.tsx">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-bold">{label}</span>
        {error && <span className="text-sm text-red-500">{error}</span>}
      </div>
      {children}
    </label>;
}
export function StandardButton({
  children,
  isLoading = false,
  thinkingMessage,
  className,
  disabled,
  ...props
}: ButtonProps & {
  children: React.ReactNode;
  isLoading?: boolean;
  thinkingMessage?: string;
}) {
  return <Button className={cn("w-full", className)} disabled={disabled || isLoading} {...props} data-sentry-element="Button" data-sentry-component="StandardButton" data-sentry-source-file="standards.tsx">
      {isLoading ? <div className="flex items-center justify-center">
          <LoadingSpinner className="w-3 h-3" />
          {thinkingMessage && <span className="ml-2 small-caps pb-[2px]">{thinkingMessage}</span>}
        </div> : children}
    </Button>;
}
export function StandardSubmitButton({
  children = "Continue",
  isLoading,
  thinkingMessage,
  className,
  disabled,
  ...props
}: Omit<ButtonProps, "type" | "href"> & {
  children?: React.ReactNode;
  thinkingMessage?: string;
  isLoading?: boolean;
}) {
  const {
    pending
  } = useFormStatus();
  return <StandardButton type="submit" isLoading={isLoading || pending} thinkingMessage={thinkingMessage} className={className} disabled={disabled} {...props} data-sentry-element="StandardButton" data-sentry-component="StandardSubmitButton" data-sentry-source-file="standards.tsx">
      {children}
    </StandardButton>;
}
export function StandardTitleSkeleton({
  className
}: {
  className?: string;
}) {
  return <div className={cn("space-y-2", className)} data-sentry-component="StandardTitleSkeleton" data-sentry-source-file="standards.tsx">
      <Skeleton className="h-6 w-full" data-sentry-element="Skeleton" data-sentry-source-file="standards.tsx" />
      <Skeleton className="h-6 w-3/4" data-sentry-element="Skeleton" data-sentry-source-file="standards.tsx" />
    </div>;
}
export function StandardSubtitleSkeleton({
  className
}: {
  className?: string;
}) {
  return <div className={cn("space-y-2", className)} data-sentry-component="StandardSubtitleSkeleton" data-sentry-source-file="standards.tsx">
      <Skeleton className="h-4 w-full" data-sentry-element="Skeleton" data-sentry-source-file="standards.tsx" />
      <Skeleton className="h-4 w-full" data-sentry-element="Skeleton" data-sentry-source-file="standards.tsx" />
      <Skeleton className="h-4 w-1/2" data-sentry-element="Skeleton" data-sentry-source-file="standards.tsx" />
    </div>;
}
export function StandardButtonSkeleton({
  className
}: {
  className?: string;
}) {
  return <Skeleton className={cn("h-10 w-full", className)} data-sentry-element="Skeleton" data-sentry-component="StandardButtonSkeleton" data-sentry-source-file="standards.tsx" />;
}
export function StandardTextareaSkeleton({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return <div className={cn("flex min-h-[80px] w-full rounded-md border border-input bg-muted", "animate-pulse", className)} {...props} data-sentry-component="StandardTextareaSkeleton" data-sentry-source-file="standards.tsx" />;
}